import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { userAPI } from "../utils/api";
import { useUser } from "./UserContext";

const BookmarkContext = createContext();

export const useBookmark = () => {
  const context = useContext(BookmarkContext);
  if (!context) {
    throw new Error("useBookmark must be used within a BookmarkProvider");
  }
  return context;
};

export const BookmarkProvider = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const [bookmarkedCards, setBookmarkedCards] = useState([]);
  const [bookmarkedCardIds, setBookmarkedCardIds] = useState(() => {
    try {
      const saved = localStorage.getItem("pms_bookmarked_cards");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  // Sync with server bookmarks on authentication
  useEffect(() => {
    if (!isAuthenticated) {
      setBookmarkedCards([]);
      setBookmarkedCardIds([]);
      return;
    }

    const fetchServerBookmarks = async () => {
      try {
        setLoading(true);
        const res = await userAPI.getBookmarks();
        if (res.data?.success) {
          const cards = res.data.bookmarks || [];
          setBookmarkedCards(cards);
          const ids = cards.map((c) => c._id || c.id).filter(Boolean);
          setBookmarkedCardIds(ids);
          localStorage.setItem("pms_bookmarked_cards", JSON.stringify(ids));
        }
      } catch (err) {
        console.error("Error fetching bookmarks from server:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchServerBookmarks();
  }, [isAuthenticated, user?._id]);

  // Helper check
  const isBookmarked = useCallback(
    (cardId) => {
      if (!cardId) return false;
      const targetId = String(cardId);
      return bookmarkedCardIds.some((id) => String(id) === targetId);
    },
    [bookmarkedCardIds]
  );

  // Add bookmark
  const addBookmark = useCallback(
    async (card) => {
      if (!card) return;
      const cardId = card._id || card.id;
      if (!cardId) return;

      const targetId = String(cardId);

      // Optimistic update
      setBookmarkedCardIds((prev) => {
        if (prev.some((id) => String(id) === targetId)) return prev;
        const next = [targetId, ...prev];
        localStorage.setItem("pms_bookmarked_cards", JSON.stringify(next));
        return next;
      });

      setBookmarkedCards((prev) => {
        if (prev.some((c) => String(c._id || c.id) === targetId)) return prev;
        return [card, ...prev];
      });

      // API sync
      try {
        const res = await userAPI.addBookmark(cardId);
        if (res.data?.success && res.data.bookmarks) {
          setBookmarkedCards(res.data.bookmarks);
          const ids = res.data.bookmarks.map((c) => c._id || c.id);
          setBookmarkedCardIds(ids);
          localStorage.setItem("pms_bookmarked_cards", JSON.stringify(ids));
        }
      } catch (err) {
        console.error("Error syncing add bookmark:", err);
      }
    },
    []
  );

  // Remove bookmark
  const removeBookmark = useCallback(
    async (cardId) => {
      if (!cardId) return;
      const targetId = String(cardId);

      // Optimistic update
      setBookmarkedCardIds((prev) => {
        const next = prev.filter((id) => String(id) !== targetId);
        localStorage.setItem("pms_bookmarked_cards", JSON.stringify(next));
        return next;
      });

      setBookmarkedCards((prev) =>
        prev.filter((c) => String(c._id || c.id) !== targetId)
      );

      // API sync
      try {
        const res = await userAPI.removeBookmark(cardId);
        if (res.data?.success && res.data.bookmarks) {
          setBookmarkedCards(res.data.bookmarks);
          const ids = res.data.bookmarks.map((c) => c._id || c.id);
          setBookmarkedCardIds(ids);
          localStorage.setItem("pms_bookmarked_cards", JSON.stringify(ids));
        }
      } catch (err) {
        console.error("Error syncing remove bookmark:", err);
      }
    },
    []
  );

  // One-click toggle
  const toggleBookmark = useCallback(
    (card) => {
      if (!card) return;
      const cardId = card._id || card.id;
      if (isBookmarked(cardId)) {
        removeBookmark(cardId);
      } else {
        addBookmark(card);
      }
    },
    [isBookmarked, addBookmark, removeBookmark]
  );

  const value = {
    bookmarkedCards,
    bookmarkedCardIds,
    isBookmarked,
    addBookmark,
    removeBookmark,
    toggleBookmark,
    loading,
  };

  return (
    <BookmarkContext.Provider value={value}>
      {children}
    </BookmarkContext.Provider>
  );
};
